/**
 * Site media library (GET/POST/PUT/DELETE /{id}/media), storage usage (GET /{id}/storage) and plans (GET /plans).
 * Exports: MediaGrid (library browser, optional pick mode), MediaPickerModal (used by editor image fields),
 * StorageBar, PlansModal, uploadMedia() and reportStorageError() (402 storage_quota_exceeded → Upgrade).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button, Card, Col, Empty, Input, Modal, Pagination, Popconfirm, Progress, Row, Segmented, Space, Spin, Tooltip, Typography, Upload, message,
} from 'antd';
import { CheckCircleFilled, DeleteOutlined, FileOutlined, PlayCircleOutlined, UploadOutlined, CopyOutlined } from '@ant-design/icons';
import { t as tt, useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';
import { QUIET, directUpload, sb, site } from './api';
import { copyText, listOf, toastErr, totalOf, useSite } from './common';

const { Text } = Typography;

export const fmtSize = (b) => {
    const n = Number(b) || 0;
    if (n < 1024) return `${n} B`;
    const u = ['KB', 'MB', 'GB', 'TB'];
    let v = n / 1024;
    let i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
    return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
};

let openPlans = null; // set by the mounted PlansModal host
export const showPlans = () => { if (openPlans) openPlans(); else window.location.href = userPath('/provision/sitebuilder'); };

/** 402 storage_quota_exceeded → one line + Upgrade; returns true when handled */
export const reportStorageError = (e) => {
    const b = e?.body || {};
    if (e?.status === 402 || b.error_code === 'storage_quota_exceeded' || b.code === 'storage_quota_exceeded') {
        const key = 'yc-storage-quota';
        message.warning({
            key,
            duration: 8,
            content: <Space>{b.error || tt('Storage is full.')}<Button size="small" type="primary" onClick={() => { message.destroy(key); showPlans(); }}>{tt('Upgrade')}</Button></Space>,
        });
        return true;
    }
    return false;
};

/** upload files → media rows: each one straight to storage, then complete. onProgress gets 0-100 over all files. */
export const uploadMedia = async (siteId, files, onProgress) => {
    const out = [];
    const api = site(siteId);
    try {
        for (let i = 0; i < files.length; i += 1) {
            const { key } = await directUpload(api, files[i], 'media', { // eslint-disable-line no-await-in-loop
                onProgress: onProgress ? (p) => onProgress(Math.round((i * 100 + p) / files.length)) : undefined,
            });
            out.push(await api.post('/media/complete', { key })); // eslint-disable-line no-await-in-loop
        }
        return out;
    } catch (e) {
        if (!reportStorageError(e)) toastErr(e, tt('Upload failed'));
        throw e;
    }
};

export const StorageBar = ({ usage, compact }) => {
    const { t } = useTranslation();
    if (!usage) return null;
    const limit = Number(usage.limit) || 0;
    const used = Number(usage.used) || 0;
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 1000) / 10) : 0;
    const parts = Object.entries(usage.breakdown || {}).filter(([, v]) => v > 0);
    const NAMES = { media: 'Media', product_images: 'Product images', versions: 'Published versions', forms: 'Form submissions', data: 'Data' };
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <Text type="secondary">{t('Storage')}</Text>
                <Text type="secondary">{fmtSize(used)} / {limit > 0 ? fmtSize(limit) : t('Unlimited')}</Text>
            </div>
            <Tooltip title={parts.length ? parts.map(([k, v]) => `${t(NAMES[k] || k)}: ${fmtSize(v)}`).join(' · ') : undefined}>
                <Progress percent={pct} showInfo={false} size="small" status={pct >= 95 ? 'exception' : 'normal'} strokeColor={pct >= 80 && pct < 95 ? '#fa8c16' : undefined} />
            </Tooltip>
            {!compact && pct >= 80 && <Button size="small" type="link" style={{ padding: 0 }} onClick={showPlans}>{t('Upgrade')}</Button>}
        </div>
    );
};

const planPrice = (p) => {
    const m = (p.prices || []).find((x) => x.billing_cycle === 'monthly') || (p.prices || [])[0];
    if (!m) return null;
    return `${formatMoney(Number(m.price), m.currency)}${m.billing_cycle === 'monthly' ? '/mo' : m.billing_cycle === 'annually' ? '/yr' : ''}`;
};

/** mount once (Overview / Media); showPlans() opens it */
export const PlansModal = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [plans, setPlans] = useState(null);
    useEffect(() => {
        openPlans = () => setOpen(true);
        return () => { openPlans = null; };
    }, []);
    useEffect(() => {
        if (open && plans === null) sb.get('/plans', null, QUIET).then((d) => setPlans(listOf(d))).catch(() => setPlans([]));
    }, [open, plans]);
    return (
        <Modal open={open} onCancel={() => setOpen(false)} footer={null} title={t('Plans')} width={880}>
            {plans === null ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : !plans.length ? <Empty /> : (
                <>
                <Row gutter={[12, 12]}>
                    {plans.filter((p) => !p.addon).map((p) => {
                        const f = p.features || {};
                        const n = (v) => (Number(v) < 0 ? t('Unlimited') : v);
                        return (
                            <Col key={p.id} xs={24} sm={12} md={6}>
                                <Card size="small" style={{ height: '100%' }}>
                                    <Text strong>{p.name}</Text>
                                    <div style={{ fontSize: 20, fontWeight: 600, margin: '6px 0' }}>{planPrice(p) || '-'}</div>
                                    <div style={{ fontSize: 13, lineHeight: 1.9 }}>
                                        <div><b>{Number(f.storage_mb) < 0 ? t('Unlimited') : fmtSize((Number(f.storage_mb) || 0) * 1048576)}</b> {t('storage')}</div>
                                        <div>{t('Sites')}: <b>{n(f.sites)}</b> · {t('Products', { context: 'shop' })}: <b>{n(f.products)}</b></div>
                                        <div>{t('Custom domains')}: <b>{n(f.custom_domains)}</b></div>
                                        {f.remove_badge && <div>{t('No Yeap badge')}</div>}
                                    </div>
                                    {(p.on_sale || planPrice(p) === null || Number((p.prices || [])[0]?.price) > 0) && <Button block type="primary" style={{ marginTop: 8 }} disabled={!p.on_sale}
                                        onClick={() => { setOpen(false); navigate(userPath(`/provision/sitebuilder?product_id=${p.id}`)); }}>
                                        {p.on_sale ? t('Choose') : t('Coming soon')}
                                    </Button>}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
                {plans.filter((p) => p.addon).map((p) => (
                    <Card key={p.id} size="small" style={{ marginTop: 12 }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                            <span><Text strong>{p.name}</Text> <Text type="secondary">{planPrice(p)}</Text></span>
                            <Button disabled={!p.on_sale} onClick={() => { setOpen(false); navigate(userPath(`/provision/sitebuilder?product_id=${p.id}`)); }}>{p.on_sale ? t('Add') : t('Coming soon')}</Button>
                        </Space>
                    </Card>
                ))}
                </>
            )}
        </Modal>
    );
};

const Thumb = ({ m, size = 120 }) => {
    if (m.kind === 'image') return <img src={m.url} alt={m.alt || ''} loading="lazy" style={{ width: '100%', height: size, objectFit: 'cover', display: 'block', background: '#f3f4f6' }} />;
    const Icon = m.kind === 'video' ? PlayCircleOutlined : FileOutlined;
    return <div style={{ height: size, display: 'grid', placeItems: 'center', background: '#f3f4f6', fontSize: 32, color: '#999' }}><Icon /></div>;
};

/** Library browser. pick: (media) => void enables pick mode (click selects). */
export const MediaGrid = ({ siteId, kind: fixedKind, onPick, pageSize = 30, onChanged }) => {
    const { t } = useTranslation();
    const [kind, setKind] = useState(fixedKind || '');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [soon, setSoon] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await sb.get(`/${siteId}/media`, { kind: kind || undefined, q: q || undefined, page, per_page: pageSize }, QUIET);
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) {
            if (e?.status === 404) setSoon(true); else toastErr(e);
            setData({ items: [], total: 0 });
        }
    }, [siteId, kind, q, page, pageSize]);
    useEffect(() => { load(); }, [load]);

    const upload = async (files) => {
        setBusy(true);
        setProgress(0);
        try {
            const rows = await uploadMedia(siteId, files, setProgress);
            message.success(t('{{n}} uploaded', { n: rows.length }));
            setPage(1);
            await load();
            onChanged?.();
            if (onPick && rows.length === 1) setSelected(rows[0]);
        } catch (_) { load(); /* reported; files before the failure did arrive */ } finally { setBusy(false); setProgress(null); }
    };
    const del = async (m) => {
        try { await sb.del(`/${siteId}/media/${m.id}`); load(); onChanged?.(); } catch (e) { toastErr(e); }
    };
    const saveAlt = async (m, alt) => {
        if ((m.alt || '') === alt) return;
        try { await sb.put(`/${siteId}/media/${m.id}`, { alt }); setData((d) => ({ ...d, items: d.items.map((x) => (x.id === m.id ? { ...x, alt } : x)) })); } catch (e) { toastErr(e); }
    };

    if (soon) return <Empty description={t('Media library is coming soon')} />;
    const accept = (fixedKind || kind) === 'image' ? 'image/*' : 'image/*,video/mp4,video/webm,application/pdf';
    return (
        <div>
            <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
                <Space wrap>
                    {!fixedKind && <Segmented value={kind} onChange={(v) => { setKind(v); setPage(1); }}
                        options={[{ value: '', label: t('All') }, { value: 'image', label: t('Images') }, { value: 'video', label: t('Videos') }, { value: 'file', label: t('Files') }]} />}
                    <Input.Search allowClear placeholder={t('Search')} style={{ width: 200 }} onSearch={(v) => { setQ(v); setPage(1); }} />
                </Space>
                <Upload multiple accept={accept} showUploadList={false}
                    beforeUpload={(file, list) => { if (file === list[0]) upload(list); return false; }}>
                    <Button type="primary" icon={<UploadOutlined />} loading={busy}>{t('Upload')}</Button>
                </Upload>
            </Space>
            {progress !== null && <Progress percent={progress} size="small" style={{ marginBottom: 8 }} />}
            <Upload.Dragger multiple accept={accept} showUploadList={false} openFileDialogOnClick={false}
                beforeUpload={(file, list) => { if (file === list[0]) upload(list); return false; }}
                style={{ padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'default' }}>
                {data === null ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                    : !data.items.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Drop files here to upload')} />
                        : (
                            <Row gutter={[10, 10]}>
                                {data.items.map((m) => (
                                    <Col key={m.id} xs={12} sm={8} md={onPick ? 6 : 4}>
                                        <div onClick={onPick ? () => setSelected(m) : undefined} onDoubleClick={onPick ? () => onPick(m) : undefined}
                                            style={{
                                                border: `2px solid ${selected?.id === m.id ? '#2357ff' : 'var(--ant-color-border-secondary, #f0f0f0)'}`, borderRadius: 8, overflow: 'hidden',
                                                cursor: onPick ? 'pointer' : 'default', position: 'relative', background: 'var(--ant-color-bg-container, #fff)',
                                            }}>
                                            <Thumb m={m} />
                                            {selected?.id === m.id && <CheckCircleFilled style={{ position: 'absolute', top: 6, right: 6, color: '#2357ff', fontSize: 18, background: '#fff', borderRadius: '50%' }} />}
                                            <div style={{ padding: 6 }} onClick={(e) => e.stopPropagation()}>
                                                <Text ellipsis style={{ fontSize: 12, display: 'block' }} title={m.name}>{m.name}</Text>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>{fmtSize(m.bytes)}{m.width ? ` · ${m.width}×${m.height}` : ''}</Text>
                                                    <Tooltip title={t('Copy URL')}><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copyText(m.url)} /></Tooltip>
                                                    {!onPick && (
                                                        <Popconfirm title={t('Delete?')} onConfirm={() => del(m)}>
                                                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                                        </Popconfirm>
                                                    )}
                                                </div>
                                                {m.kind === 'image' && !onPick && (
                                                    <Input size="small" variant="borderless" placeholder={t('Alt text')} defaultValue={m.alt || ''} style={{ padding: 0, fontSize: 12 }}
                                                        onBlur={(e) => saveAlt(m, e.target.value)} onPressEnter={(e) => e.target.blur()} />
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        )}
            </Upload.Dragger>
            {data && data.total > pageSize && (
                <Pagination style={{ marginTop: 12, textAlign: 'right' }} size="small" current={page} pageSize={pageSize} total={data.total} onChange={setPage} showSizeChanger={false} />
            )}
            {onPick && (
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Button type="primary" disabled={!selected} onClick={() => onPick(selected)}>{t('Select')}</Button>
                </div>
            )}
        </div>
    );
};

export const MediaPickerModal = ({ open, siteId, kind = 'image', onClose, onPick }) => {
    const { t } = useTranslation();
    return (
        <Modal open={open} onCancel={onClose} footer={null} width={860} title={t('Media library')} destroyOnHidden zIndex={1100}>
            <MediaGrid siteId={siteId} kind={kind} pageSize={24} onPick={(m) => { onPick(m); onClose(); }} />
        </Modal>
    );
};

const Media = () => {
    const { t } = useTranslation();
    const { siteId } = useSite();
    const [usage, setUsage] = useState(null);
    const loadUsage = useCallback(() => { sb.get(`/${siteId}/storage`, null, QUIET).then(setUsage).catch(() => setUsage(null)); }, [siteId]);
    useEffect(() => { loadUsage(); }, [loadUsage]);
    return (
        <Card size="small" title={t('Media')} extra={usage && <div style={{ width: 240 }}><StorageBar usage={usage} compact /></div>}>
            <MediaGrid siteId={siteId} onChanged={loadUsage} />
            <PlansModal />
        </Card>
    );
};

export default Media;
